import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

export default function PrivacyPage() {
    const { t, language } = useI18n();
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const content = language === 'es' ? {
        title: 'Política de Privacidad',
        lastUpdated: 'Última actualización: 27 de enero de 2026',
        intro: 'En MealPlanner, nos tomamos muy en serio tu privacidad. Esta política explica cómo recopilamos, utilizamos y protegemos tu información personal.',
        sections: [
            {
                title: '1. Información que recopilamos',
                content: 'Recopilamos información que nos proporcionas directamente al registrarte, como tu nombre, correo electrónico y preferencias alimentarias. También recopilamos datos sobre las recetas que creas y los menús que generas.'
            },
            {
                title: '2. Uso de la información',
                content: 'Utilizamos tu información para proporcionarte nuestros servicios, personalizar tu experiencia (como la generación de menús mediante IA), procesar pagos y comunicarnos contigo sobre actualizaciones o soporte.'
            },
            {
                title: '3. Servicios de terceros',
                content: 'Utilizamos servicios de terceros como OpenAI para la generación de contenido e IA, TheMealDB para la búsqueda de imágenes y Stripe para el procesamiento de pagos. Estos terceros tienen sus propias políticas de privacidad y solo acceden a la información necesaria para realizar sus funciones.'
            },
            {
                title: '4. Seguridad de los datos',
                content: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tus datos contra el acceso no autorizado, la pérdida o la alteración. Sin embargo, ninguna transmisión por Internet es 100% segura.'
            },
            {
                title: '5. Tus derechos',
                content: 'Tienes derecho a acceder, rectificar o eliminar tus datos personales en cualquier momento. Puedes hacerlo directamente desde los ajustes de tu perfil o contactándonos.'
            },
            {
                title: '6. Cambios en esta política',
                content: 'Podemos actualizar esta política ocasionalmente. Te notificaremos sobre cambios significativos publicando la nueva política en esta página.'
            }
        ],
        contact: 'Si tienes preguntas sobre esta política, contáctanos a través de nuestro soporte.'
    } : {
        title: 'Privacy Policy',
        lastUpdated: 'Last updated: January 27, 2026',
        intro: 'At MealPlanner, we take your privacy very seriously. This policy explains how we collect, use, and protect your personal information.',
        sections: [
            {
                title: '1. Information We Collect',
                content: 'We collect information you provide directly to us when registering, such as your name, email, and dietary preferences. We also collect data about the recipes you create and the menus you generate.'
            },
            {
                title: '2. Use of Information',
                content: 'We use your information to provide our services, personalize your experience (such as AI-powered menu generation), process payments, and communicate with you about updates or support.'
            },
            {
                title: '3. Third-Party Services',
                content: 'We use third-party services such as OpenAI for content and AI generation, TheMealDB for image search, and Stripe for payment processing. These third parties have their own privacy policies and only access information necessary to perform their functions.'
            },
            {
                title: '4. Data Security',
                content: 'We implement technical and organizational security measures to protect your data against unauthorized access, loss, or alteration. However, no internet transmission is 100% secure.'
            },
            {
                title: '5. Your Rights',
                content: 'You have the right to access, rectify, or delete your personal data at any time. You can do this directly from your profile settings or by contacting us.'
            },
            {
                title: '6. Changes to This Policy',
                content: 'We may update this policy occasionally. We will notify you of significant changes by posting the new policy on this page.'
            }
        ],
        contact: 'If you have questions about this policy, contact us through our support.'
    };

    return (
        <div className="min-h-screen bg-cream texture-noise">
            <div className="max-w-4xl mx-auto px-6 py-12 md:py-20">
                <Button
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    className="mb-8 text-stone hover:text-charcoal -ml-4"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('back')}
                </Button>

                <header className="mb-12">
                    <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                        <ShieldCheck className="w-8 h-8 text-primary" strokeWidth={1.5} />
                    </div>
                    <h1 className="font-manrope font-bold text-4xl md:text-5xl text-charcoal mb-4">
                        {content.title}
                    </h1>
                    <p className="font-inter text-stone">
                        {content.lastUpdated}
                    </p>
                </header>

                <section className="bg-white rounded-3xl shadow-soft p-8 md:p-12 space-y-12">
                    <p className="font-inter text-lg text-charcoal leading-relaxed border-b border-border pb-8">
                        {content.intro}
                    </p>

                    <div className="grid gap-12">
                        {content.sections.map((section, idx) => (
                            <div key={idx} className="space-y-4">
                                <h2 className="font-manrope font-semibold text-xl text-charcoal">
                                    {section.title}
                                </h2>
                                <p className="font-inter text-stone leading-relaxed">
                                    {section.content}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="pt-8 border-t border-border">
                        <p className="font-inter text-stone italic">
                            {content.contact}
                        </p>
                    </div>
                </section>

                <footer className="mt-12 text-center">
                    <p className="font-manrope font-bold text-charcoal text-lg mb-2">MealPlanner</p>
                    <p className="font-inter text-sm text-stone">© 2026 MealPlanner. All rights reserved.</p>
                </footer>
            </div>
        </div>
    );
}
